/**
 * §9 — the player answer screen.
 *
 * §9.1: the prompt, a `Get Ready` indicator, the typed field, `Reveal Choices`,
 * and Submit are all visible during the reading window, but no submission is
 * accepted until the countdown starts (§8.3).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCountdown, useReadingWindow } from "@tq/ui";
import type { EventPayload } from "@tq/protocol";
import type { ConnectionState } from "@tq/realtime/client";
import { api, ApiRequestError } from "../api.js";
import { buildIdempotencyKey } from "./idempotency.js";

export interface AnswerScreenProps {
  payload: EventPayload<"question.opened">;
  roundToken: string | null;
  sessionId: string;
  clockOffsetMs: number;
  connectionState: ConnectionState;
  onSubmitted: () => void;
  onError: (message: string | null) => void;
}

interface Choice {
  id: string;
  choiceNumber: number;
  choiceText: string;
}

export function AnswerScreen({
  payload,
  roundToken: initialToken,
  sessionId,
  clockOffsetMs,
  connectionState,
  onSubmitted,
  onError,
}: AnswerScreenProps): React.ReactElement {
  const reading = useReadingWindow(payload.opensAt, clockOffsetMs);
  const countdown = useCountdown(payload.closesAt, payload.timeLimitSeconds * 1000, clockOffsetMs);

  const [token, setToken] = useState<string | null>(initialToken);
  const [text, setText] = useState("");
  const [choices, setChoices] = useState<Choice[] | null>(null);
  const [selectedChoiceId, setSelectedChoiceId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // §37.2 telemetry, gathered on the device and sent once with the submission.
  const firstInputAt = useRef<number | null>(null);
  const revealedAt = useRef<number | null>(null);
  const editCount = useRef(0);
  const autoSubmitAttempted = useRef(false);
  const idempotencyKey = useMemo(() => buildIdempotencyKey(payload.roundId), [payload.roundId]);

  const typedAllowed = payload.answerMode === "Both" || payload.answerMode === "TextOnly";
  const choicesAllowed =
    payload.answerMode === "Both" || payload.answerMode === "MultipleChoiceOnly";
  // §9.3 — revealing choices is irreversible for that question.
  const revealed = choices != null;
  const locked = submitted || submitting;

  // The token is needed to submit; fetch it if the event arrived first.
  useEffect(() => {
    if (token || !sessionId) return;
    let cancelled = false;
    void api
      .playerState(sessionId)
      .then((state) => {
        if (!cancelled && state.roundToken) setToken(state.roundToken);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [token, sessionId]);

  const submit = useCallback(
    async (input: { text: string | null; choiceId: string | null; autoSubmitted: boolean }) => {
      if (!token || locked) return;

      // §29.3 — do not silently accept an answer while the client is offline.
      if (connectionState === "disconnected") {
        onError("You are offline. Reconnect to send your answer.");
        return;
      }

      setSubmitting(true);
      onError(null);
      try {
        await api.submitAnswer({
          roundToken: token,
          text: input.text,
          choiceId: input.choiceId,
          clientSubmittedAt: Date.now(),
          firstInputAt: firstInputAt.current,
          revealedChoicesAt: revealedAt.current,
          editCount: editCount.current,
          autoSubmitted: input.autoSubmitted,
          idempotencyKey,
        });
        setSubmitted(true);
        onSubmitted();
      } catch (cause) {
        const message =
          cause instanceof ApiRequestError
            ? cause.message
            : "That answer could not be sent. Try again.";
        // §26.5 — on a closed question the client stops retrying and moves on.
        if (cause instanceof ApiRequestError && cause.code === "QUESTION_CLOSED") {
          setSubmitted(true);
          onError(message);
          onSubmitted();
        } else if (cause instanceof ApiRequestError && cause.code === "ALREADY_ANSWERED") {
          setSubmitted(true);
          onSubmitted();
        } else {
          onError(message);
        }
      } finally {
        setSubmitting(false);
      }
    },
    [token, locked, connectionState, idempotencyKey, onError, onSubmitted],
  );

  /**
   * §9.2 / P-020 — if time expires with non-empty text, the client attempts one
   * automatic submission. Blank text is never auto-submitted (§9.5).
   */
  useEffect(() => {
    if (reading || submitted || submitting || autoSubmitAttempted.current) return;
    if (!countdown.expired) return;
    autoSubmitAttempted.current = true;
    if (selectedChoiceId) {
      void submit({ text: null, choiceId: selectedChoiceId, autoSubmitted: true });
    } else if (typedAllowed && !revealed && text.trim().length > 0) {
      void submit({ text, choiceId: null, autoSubmitted: true });
    } else {
      onSubmitted();
    }
  }, [
    countdown.expired,
    reading,
    submitted,
    submitting,
    selectedChoiceId,
    text,
    typedAllowed,
    revealed,
    submit,
    onSubmitted,
  ]);

  const handleReveal = useCallback(async () => {
    if (!token || revealed || locked) return;
    revealedAt.current = Date.now();
    try {
      const result = await api.revealChoices(token);
      setChoices(result.choices);
    } catch (cause) {
      onError(cause instanceof ApiRequestError ? cause.message : "Could not load the choices.");
      revealedAt.current = null;
    }
  }, [token, revealed, locked, onError]);

  /**
   * §9.1 / P-018 — the phone keyboard's return key submits.
   *
   * On a phone the green button is the thing the on-screen keyboard covers, so
   * the return key the player's thumb is already resting on has to do the same
   * job. The guards mirror the button's `disabled` exactly: nothing blank, and
   * nothing during the reading window (§8.3).
   */
  const handleTypedKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== "Enter") return;
      // A phone IME composing a word sends Enter to accept the candidate, not
      // to send the answer.
      if (event.nativeEvent.isComposing) return;
      event.preventDefault();
      if (reading || locked || text.trim().length === 0) return;
      void submit({ text, choiceId: null, autoSubmitted: false });
    },
    [reading, locked, text, submit],
  );

  const handleChoice = useCallback(
    (choiceId: string) => {
      if (locked || reading) return;
      // §9.3 / P-025 — selecting a choice submits it immediately, once.
      setSelectedChoiceId(choiceId);
      void submit({ text: null, choiceId, autoSubmitted: false });
    },
    [locked, reading, submit],
  );

  const ringSeconds = reading
    ? "—"
    : `0:${countdown.seconds < 10 ? "0" : ""}${Math.max(countdown.seconds, 0)}`;
  // The mockup's ring border tracks the clock from green through gold and orange
  // to red, so a player reads the time left from the color before the number.
  const ringColor = reading ? "var(--gold)" : timerColor(countdown.fraction);

  return (
    <>
      {/* P-015 — the category and value, consistent with the public board. */}
      <div className="tq-phone-cat-row">
        <span className="tq-phone-cat-chip">{payload.categoryName}</span>
        <span className="tq-phone-value">
          {payload.valuePrefix}
          {payload.cellValue}
        </span>
      </div>

      <div className="tq-phone-timer-wrap" role="status" aria-live="polite">
        <div
          className={`tq-phone-timer-ring ${!reading && countdown.seconds <= 3 ? "tq-phone-timer-ring--urgent" : ""}`}
          style={{ borderColor: ringColor }}
        >
          {ringSeconds}
        </div>
        <div className="tq-phone-timer-lbl">{reading ? "GET READY" : "TIME REMAINING"}</div>
        {!reading && (
          <span className="tq-visually-hidden">{countdown.seconds} seconds remaining</span>
        )}
      </div>

      {/* §8.2 — the full prompt, as text. §29.1 — it stays visible while
          answering, and does not scroll horizontally (P-013). */}
      <p className="tq-phone-prompt">{payload.prompt}</p>

      {submitted ? (
        // §29.1 / P-026 — a clear submitted state.
        <div className="tq-phone-result-badge tq-phone-result-badge--correct">
          ✓ Answer submitted
          <div style={{ fontWeight: 700, fontSize: 13, marginTop: 4 }}>
            Waiting for the question to close.
          </div>
        </div>
      ) : revealed ? (
        <div className="tq-choices">
          {choices!.map((choice) => (
            <button
              key={choice.id}
              type="button"
              className={`tq-choice ${selectedChoiceId === choice.id ? "tq-choice--selected" : ""}`}
              disabled={locked || reading}
              onClick={() => handleChoice(choice.id)}
            >
              {choice.choiceText}
            </button>
          ))}
        </div>
      ) : (
        <>
          {typedAllowed && (
            <>
              <div className="tq-phone-label">ANSWER FROM MEMORY</div>
              <input
                className="tq-answer-input"
                type="text"
                inputMode="text"
                autoComplete="off"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                maxLength={250}
                aria-label="Answer from memory"
                /* Labels the phone's return key "Send" rather than "return",
                   so the key that submits says so before it is pressed. */
                enterKeyHint="send"
                onKeyDown={handleTypedKeyDown}
                // Answers come from memory: no pasting or dropping text in.
                onPaste={(event) => event.preventDefault()}
                onDrop={(event) => event.preventDefault()}
                // §8.3 / §9.1 — visible during the reading window, but locked.
                disabled={reading || locked}
                placeholder={reading ? "Read the question…" : "Type your answer here…"}
                value={text}
                onChange={(event) => {
                  // §22.5 / P-019 — keystrokes are never transmitted. Only the
                  // edit count travels, and only with the submission.
                  firstInputAt.current ??= Date.now();
                  editCount.current += 1;
                  setText(event.target.value);
                }}
              />
            </>
          )}

          {typedAllowed && choicesAllowed && <div className="tq-phone-or">— OR —</div>}

          {choicesAllowed && (
            <button
              type="button"
              className="tq-phone-reveal"
              disabled={reading || locked}
              onClick={() => void handleReveal()}
            >
              <span>🔀 Reveal Choices</span>
              {/* §9.3 — the tradeoff is explicit before the player commits. */}
              {typedAllowed && <span className="tq-warn">forfeits typed bonus</span>}
            </button>
          )}
        </>
      )}

      {/* §29.1 / P-018 — the submit control stays reachable above the keyboard.
          It used to be pushed to the bottom of the column with `marginTop:
          auto`, which is the opposite of that rule on a phone: the keyboard
          comes up over the bottom of the viewport and the button is the first
          thing it covers, with a hand's width of empty purple above it. Sitting
          directly under `Reveal Choices` keeps it in the part of the screen the
          keyboard leaves alone, and the return key submits as well. */}
      {!submitted && !revealed && typedAllowed && (
        <button
          type="button"
          className="tq-phone-submit"
          disabled={reading || locked || text.trim().length === 0}
          onClick={() => void submit({ text, choiceId: null, autoSubmitted: false })}
        >
          {submitting ? "SENDING…" : reading ? "GET READY…" : "SUBMIT ANSWER"}
        </button>
      )}
    </>
  );
}

/** The mockup's timer ramp: green → gold → orange → red as the clock runs out. */
function timerColor(fraction: number): string {
  const stops: Array<[number, [number, number, number]]> = [
    [1, [30, 224, 107]],
    [0.66, [247, 209, 10]],
    [0.33, [251, 146, 60]],
    [0, [229, 34, 46]],
  ];
  for (let i = 0; i < stops.length - 1; i += 1) {
    const [hiStop, hiColor] = stops[i]!;
    const [loStop, loColor] = stops[i + 1]!;
    if (fraction <= hiStop && fraction >= loStop) {
      const span = hiStop - loStop;
      const t = span === 0 ? 0 : (hiStop - fraction) / span;
      const mix = hiColor.map((channel, index) =>
        Math.round(channel + (loColor[index]! - channel) * t),
      );
      return `rgb(${mix.join(",")})`;
    }
  }
  return `rgb(${stops[stops.length - 1]![1].join(",")})`;
}
